import * as THREE from 'three'
import GlassBaseHole from '@glass-designer/model/glassBaseHole'
import GlassSegment from '@/model/glassSegment'

import MathsUtils from '@/utils/mathsUtils'

import GlassDesignerTypes from '@glass-designer/types'

/*
 * CustomHole
 * Custom shape hole from a stored template library
 */
export default class CustomHole extends GlassBaseHole {
  constructor(editor, options = {}) {
    super(editor, options)
    this.objectClassName = 'CustomHole'
    
    this.name = 'Custom Cutout'
    this.holeType = GlassDesignerTypes.LibraryItemShapeTypes.custom
    this.isOpenShape = !!options.isOpenShape
    this.isCornerShape = !!options.isCornerShape
    this.shapeAngle = 0
    this.openingAngle = 0

    // Build the source segments
    this.sourceSegmentList = (options.segmentList || []).map(segmentInfo => {
      var newSeg = new GlassSegment(segmentInfo.type || GlassDesignerTypes.SegmentTypes.line, segmentInfo)
      newSeg.startPosition.set(segmentInfo.startPosition.x, segmentInfo.startPosition.y)
      newSeg.endPosition.set(segmentInfo.endPosition.x, segmentInfo.endPosition.y)
      newSeg.arcUpdated()

      return newSeg
    })


    // Calculate opening vertices & the angle
    // The shape must be clockwise winding (set during item creation)
    // Rotate segments around 1st vertex so the opening faces the top
    // Note: Assumes the 1st vertex is 0,0 & the rest of the segments are offset from this origin
    // This MUST be done by the code that prepares & adds the item to the library
    if(this.isOpenShape && !this.isCornerShape) {      
      var startVert = this.sourceSegmentList[0].startPosition
      var endVert = this.sourceSegmentList[this.sourceSegmentList.length - 1].endPosition
      
      let openAngleRads = Math.atan2(endVert.y - startVert.y, endVert.x - startVert.x)
      var openAngleDegrees = 180 - MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(openAngleRads))

      // Store the opening adjustment angle so we can apply to any center cutouts included with a library item
      this.openingAngle = openAngleDegrees
      
      // Rotate the shape so the open end is up top at Y-
      var rotateOrigin = new THREE.Vector2(0, 0)
      var rotateRads = MathsUtils.degreesToRadians(openAngleDegrees)

      this.sourceSegmentList.forEach(sourceSegment => {
        let startPos = MathsUtils.rotate2DPoint(rotateOrigin, sourceSegment.startPosition, rotateRads)
        let endPos = MathsUtils.rotate2DPoint(rotateOrigin, sourceSegment.endPosition, rotateRads)
        let arcCenter = MathsUtils.rotate2DPoint(rotateOrigin, sourceSegment.arcCenterPosition, rotateRads)

        let startCornerRadiusDelta = MathsUtils.rotate2DPoint(rotateOrigin, sourceSegment.startCornerRadiusDelta, rotateRads)
        let endCornerRadiusDelta = MathsUtils.rotate2DPoint(rotateOrigin, sourceSegment.endCornerRadiusDelta, rotateRads)

        sourceSegment.startPosition = startPos
        sourceSegment.endPosition = endPos
        sourceSegment.startCornerRadiusDelta = startCornerRadiusDelta
        sourceSegment.endCornerRadiusDelta = endCornerRadiusDelta
        
        sourceSegment.arcCenterPosition = arcCenter
        sourceSegment.arcStartAngle = MathsUtils.wrapAngleDegrees(sourceSegment.arcStartAngle + openAngleDegrees)
        sourceSegment.arcEndAngle = MathsUtils.wrapAngleDegrees(sourceSegment.arcEndAngle + openAngleDegrees)
        sourceSegment.arcUpdated()
      })
    }

    this.rebuildHoleShape()
  }


  /**
   * Update the source segment list & rebuild the shape
   */
  updateSourceSegmentList(segmentList) {
    this.sourceSegmentList = segmentList.map(segmentInfo => {
      var newSeg = new GlassSegment(segmentInfo.type || GlassDesignerTypes.SegmentTypes.line, segmentInfo)
      newSeg.startPosition.set(segmentInfo.startPosition.x, segmentInfo.startPosition.y)
      newSeg.endPosition.set(segmentInfo.endPosition.x, segmentInfo.endPosition.y)
      newSeg.arcUpdated()

      return newSeg
    })

    this.rebuildHoleShape()
  }


  /**
   * Rotate the hole
   * Overriden by the shape classes
   */
  rotateHole(angleDegrees) {
    var angleRads = MathsUtils.degreesToRadians(angleDegrees)
    var bounds = MathsUtils.calculateGlassSegmentListBounds(this.sourceSegmentList)
    var boundsCenter = bounds.getCenter(new THREE.Vector2())

    // Rotate source segments around center point
    MathsUtils.rotateSegmentList(this.sourceSegmentList, boundsCenter, angleRads)

    this.rebuildHoleShape()
    super.rotateHole(angleDegrees)
  }


  /**
   * Rebuilds the hole segments based on the source segment details & the anchor info
   */
  rebuildHoleShape() {
    var startX = 0
    var startY = 0

    // Update via datum links
    this.updateDatumLinkWorldPoint()
    let sourceBounds = MathsUtils.calculateGlassSegmentListBounds(this.sourceSegmentList)

    if(this.isOpenShape && !this.isCornerShape) {
      if(this.sourceSegmentList.length > 0) {
        let entryPos = this.sourceSegmentList[0].startPosition
        let exitPos = this.sourceSegmentList[this.sourceSegmentList.length-1].endPosition
        sourceBounds.min.x = Math.min(entryPos.x, exitPos.x)
        sourceBounds.max.x = Math.max(entryPos.x, exitPos.x)
      }
    }    

    if(this.datumDisplayPointObj)
      this.datumDisplayPointObj.setPosition(this.worldPoint)

    if(this.isCornerShape) {
      // Corner shapes are all based around the 1st vertex
      startX = this.worldPoint.x
      startY = this.worldPoint.y
    } else {
      startX = this.worldPoint.x - this.anchorPoint.x - sourceBounds.min.x
      startY = this.worldPoint.y - this.anchorPoint.y - sourceBounds.min.y
    }
   
    this.segmentList = []

    // Build from the source segments & offset by the anchor point / datum links
    this.sourceSegmentList.forEach(sourceSegment => {
      let segment = new GlassSegment(sourceSegment.type, {
        startCornerRadius: sourceSegment.startCornerRadius,
        endCornerRadius: sourceSegment.endCornerRadius,
        startPosition: new THREE.Vector2(sourceSegment.startPosition.x + startX, sourceSegment.startPosition.y + startY),
        endPosition: new THREE.Vector2(sourceSegment.endPosition.x + startX, sourceSegment.endPosition.y + startY),

        startCornerRadiusDelta: sourceSegment.startCornerRadiusDelta,
        endCornerRadiusDelta: sourceSegment.endCornerRadiusDelta,

        isCutoutPolished: sourceSegment.isCutoutPolished,

        arcRadius: sourceSegment.arcRadius,
        arcStartAngle: sourceSegment.arcStartAngle,
        arcEndAngle: sourceSegment.arcEndAngle,
        arcClockwise: sourceSegment.arcClockwise,
        arcCenterPosition: new THREE.Vector2(sourceSegment.arcCenterPosition.x + startX, sourceSegment.arcCenterPosition.y + startY)
      })

      this.segmentList.push(segment)
    })

    this.updateSceneObject()
  }


  /**
   * Checks if a 2D circle intersects with the custom hole
   * Returns true if an intersection occurred & sets the closest point on the hole the point is to
   */
  check2DCircleIntersection(point, radius, outIntPoint) {
    // Point inside?
    if(MathsUtils.pointIn2DPolygon(point, this.cachedPointList)) {
      outIntPoint.set(point.x, point.y)
      return true
    }

    // Closest point within the radius?
    let closestPoint = MathsUtils.calculateClosestPointOn2DPolygon(point, this.cachedPointList)
    if(!closestPoint)
      return false

    if(closestPoint.distanceTo(point) <= radius) {
      outIntPoint.set(closestPoint.x, closestPoint.y)
      return true
    }

    return false
  }


  /**
   * Clone the custom hole
   */
  clone() {    
    var newHole = new CustomHole(this.editor)
    newHole.copy(this)

    return newHole
  }


  /**
   * Copy details from a custom hole to this custom hole
   */
  copy(otherHole) {
    super.copy(otherHole)
    
    this.sourceSegmentList = otherHole.sourceSegmentList.map(segment => {
      return segment.clone()
    })
    
    this.rebuildHoleShape()
  }


  /**
   * Update the anchor point based on the measure to types
   * For custom holes, this is based on the source segment list, not the adjusted/rotated segments
   */
  setAnchorInfo(offsetHorizontalType, offsetVerticalType) {
    this.measureToHorizontal = offsetHorizontalType
    this.measureToVertical = offsetVerticalType

    // Calculate the point based on segment bounds
    // Note: Anchor points are relative to the source hole data
    let sourceBounds = MathsUtils.calculateGlassSegmentListBounds(this.sourceSegmentList)

    // For open shapes & not corner holes, use the entry/exit positions to measure to on the horizontal axis
    if(this.isOpenShape && !this.isCornerShape) {
      if(this.sourceSegmentList.length > 0) {
        let entryPos = this.sourceSegmentList[0].startPosition
        let exitPos = this.sourceSegmentList[this.sourceSegmentList.length-1].endPosition
        sourceBounds.min.x = Math.min(entryPos.x, exitPos.x)
        sourceBounds.max.x = Math.max(entryPos.x, exitPos.x)
      }
    }

    let offsetPoint = MathsUtils.calculateHoleOffsetPoint(sourceBounds, this.measureToHorizontal, this.measureToVertical)

    this.anchorPoint.x = offsetPoint.x
    this.anchorPoint.y = offsetPoint.y

    this.rebuildHoleShape()
  }


  /**
   * Mirror the hole shape
   */
  mirrorHole(origin, mirrorX, mirrorY) {
    // Mirror source segments around 0,0 origin point
    MathsUtils.mirrorSegmentList(this.sourceSegmentList, new THREE.Vector2(0, 0), mirrorX, mirrorY)

    this.rebuildHoleShape()
    super.mirrorHole(origin, mirrorX, mirrorY)
  }


  /**
   * Calculate the width of the gap between entry/exit points of an open shape
   * Based on source segments which are rotated to ensure the gap is always pointing up (-Y)
   */
  calculateOpenShapeGapWidth() {
    if(!this.isOpenShape || this.sourceSegmentList.length == 0)
      return 0
    
    let width = this.sourceSegmentList[this.sourceSegmentList.length-1].endPosition.x - this.sourceSegmentList[0].startPosition.x
    return Math.abs(width)
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var objData = super.serialize()

    objData.sourceSegmentList = (this.sourceSegmentList || []).map(segment => segment.serialize())

    return objData
  }
  deserialize(object) {
    super.deserialize(object)

    this.sourceSegmentList = (object.sourceSegmentList || []).map(segmentInfo => {
      var newSeg = new GlassSegment(segmentInfo.type || GlassDesignerTypes.SegmentTypes.line, segmentInfo)

      newSeg.deserialize(segmentInfo)
      return newSeg
    })

    this.rebuildHoleShape()
  }



}
